// Reator central do HUD (Nível A). Canvas2D reativo ao nível de áudio do mic
// (via getLevel) + ao estado do agente. Sem dependência nova.
import { useEffect, useRef } from 'react';
import type { VoiceStatus } from '../ai/useRealtimeVoice.js';

interface Props {
  status: VoiceStatus;
  getLevel: () => number;
  size?: number;
}

const COLOR: Record<string, [number, number, number]> = {
  idle: [120, 140, 160],
  connecting: [251, 191, 36],
  listening: [57, 255, 20],
  thinking: [0, 245, 255],
  speaking: [255, 0, 229],
  error: [255, 77, 106],
};

export function VoiceReactor({ status, getLevel, size = 300 }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rafRef = useRef<number>(0);
  const levelRef = useRef(0);
  const phaseRef = useRef(0);
  const statusRef = useRef<VoiceStatus>(status);
  statusRef.current = status;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    ctx.scale(dpr, dpr);
    const cx = size / 2;
    const cy = size / 2;
    let running = true;

    const draw = (t: number) => {
      if (!running) return;
      const st = statusRef.current;
      const [r, g, b] = COLOR[st] ?? COLOR.idle!;
      const speaking = st === 'speaking';
      const thinking = st === 'thinking';

      const raw = st === 'listening' ? getLevel() : 0;
      const pulse = speaking
        ? 0.5 + 0.5 * Math.sin(t / 120)
        : thinking
          ? 0.3 + 0.2 * Math.sin(t / 200)
          : 0.12 + 0.08 * Math.sin(t / 600);
      const target = Math.max(raw, pulse * (st === 'idle' ? 0.4 : 1));
      levelRef.current += (target - levelRef.current) * 0.18;
      const lvl = levelRef.current;
      phaseRef.current += thinking ? 0.03 : speaking ? 0.02 : 0.006;
      const ph = phaseRef.current;

      ctx.clearRect(0, 0, size, size);

      // Halo
      const grd = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.48);
      grd.addColorStop(0, `rgba(${r},${g},${b},${0.26 + lvl * 0.4})`);
      grd.addColorStop(0.3, `rgba(${r},${g},${b},${0.07 + lvl * 0.12})`);
      grd.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = grd;
      ctx.fillRect(0, 0, size, size);

      // Anel segmentado (rotação)
      const ringR = size * 0.3;
      const seg = 28;
      for (let i = 0; i < seg; i++) {
        const a = ph + (i / seg) * Math.PI * 2;
        const on = i % 3 !== 0;
        const len = 8 + lvl * 16 * (on ? 1 : 0.4);
        const x1 = cx + Math.cos(a) * ringR;
        const y1 = cy + Math.sin(a) * ringR;
        const x2 = cx + Math.cos(a) * (ringR + len);
        const y2 = cy + Math.sin(a) * (ringR + len);
        ctx.strokeStyle = `rgba(${r},${g},${b},${on ? 0.6 : 0.2})`;
        ctx.lineWidth = on ? 2 : 1;
        ctx.beginPath();
        ctx.moveTo(x1, y1);
        ctx.lineTo(x2, y2);
        ctx.stroke();
      }

      // Anel interno + arcos (contra-rotação)
      ctx.strokeStyle = `rgba(${r},${g},${b},0.32)`;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(cx, cy, size * 0.22, 0, Math.PI * 2);
      ctx.stroke();
      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-ph * 1.4);
      ctx.strokeStyle = `rgba(${r},${g},${b},0.7)`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.245, 0, Math.PI * 0.5);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(0, 0, size * 0.245, Math.PI, Math.PI * 1.5);
      ctx.stroke();
      ctx.restore();

      // Núcleo
      const coreR = size * 0.16 * (1 + lvl * 0.5);
      const cg = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
      cg.addColorStop(0, 'rgba(255,255,255,0.9)');
      cg.addColorStop(0.4, `rgba(${r},${g},${b},0.85)`);
      cg.addColorStop(1, `rgba(${r},${g},${b},0)`);
      ctx.fillStyle = cg;
      ctx.beginPath();
      ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
      ctx.fill();

      rafRef.current = requestAnimationFrame(draw);
    };
    rafRef.current = requestAnimationFrame(draw);
    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
    };
  }, [size, getLevel]);

  return <canvas ref={canvasRef} style={{ width: size, height: size, display: 'block' }} />;
}
